"use client";

// Inline reference chips (skill "/" and artifact "@" mentions) rendered in
// the editor and in transcript messages. These moved here from
// `components/ai-elements/message.tsx` (which now re-exports them) so the
// registry-distributed block is self-contained — the block cannot depend on
// the non-registry ai-elements module. Single source; edit here, never fork.

import { cn } from "@workspace/ui/lib/utils";
import type { HTMLAttributes, ReactNode } from "react";

export type MessageSkillReferenceProps = HTMLAttributes<HTMLSpanElement> & {
  skillName: string;
  skillDescription?: string;
  icon?: ReactNode;
  onRemove?: () => void;
};

export function MessageSkillReference({
  skillName,
  skillDescription: _skillDescription,
  icon,
  onRemove,
  className,
  ...props
}: MessageSkillReferenceProps) {
  return (
    <span
      className={cn(
        "group/skill-ref -my-0.5 inline-flex items-center gap-0.5 rounded border border-primary/20 bg-primary/10 px-1 text-primary transition-colors hover:bg-primary/15",
        className
      )}
      {...props}
    >
      {icon && (
        <span className="inline-flex size-3 shrink-0 items-center justify-center align-middle [&>span>svg]:size-3! [&>svg]:size-3!">
          {onRemove ? (
            <>
              <span className="contents group-hover/skill-ref:hidden">
                {icon}
              </span>
              <button
                aria-label={`Remove ${skillName}`}
                className="hidden size-3 items-center justify-center bg-transparent p-0 text-primary/60 hover:text-destructive group-hover/skill-ref:inline-flex"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                type="button"
              >
                <svg
                  className="size-3!"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <title>Remove</title>
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : (
            icon
          )}
        </span>
      )}
      {icon && " "}
      {skillName}
    </span>
  );
}

export type MessageArtifactReferenceProps = HTMLAttributes<HTMLSpanElement> & {
  artifactName: string;
  icon?: ReactNode;
  onRemove?: () => void;
};

export function MessageArtifactReference({
  artifactName,
  icon,
  onRemove,
  className,
  ...props
}: MessageArtifactReferenceProps) {
  return (
    <span
      className={cn(
        "group/artifact-ref -my-0.5 inline-flex items-center gap-0.5 rounded border border-border bg-accent/60 px-1 text-accent-foreground transition-colors hover:bg-accent",
        className
      )}
      {...props}
    >
      {icon && (
        <span className="inline-flex size-3 shrink-0 items-center justify-center align-middle [&>span>svg]:size-3! [&>svg]:size-3!">
          {onRemove ? (
            <>
              <span className="contents group-hover/artifact-ref:hidden">
                {icon}
              </span>
              <button
                aria-label={`Remove ${artifactName}`}
                className="hidden size-3 items-center justify-center bg-transparent p-0 text-foreground/60 hover:text-destructive group-hover/artifact-ref:inline-flex"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                type="button"
              >
                <svg
                  className="size-3!"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <title>Remove</title>
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : (
            icon
          )}
        </span>
      )}
      {icon && " "}
      {artifactName}
    </span>
  );
}
